import { Box, Paper } from "@mui/material"
import { AddOutlined, Download2FilledOutlined, FilterAltOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { OnChangeFn, SortingState } from "@tanstack/react-table"
import type { FC } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { AddGlTransactionDialog } from "./components/AddGlTransactionDialog"
import { EditGlTransactionDialog } from "./components/EditGlTransactionDialog"
import { ExportGlTransactionsDialog } from "./components/ExportGlTransactionsDialog"
import { TransactionsFilterChips } from "./components/filters/TransactionsFilterChips"
import { TransactionsFilterDrawer } from "./components/filters/TransactionsFilterDrawer"
import { TransactionDocumentsDialog } from "./components/TransactionDocumentsDialog"
import { TransactionsTable } from "./components/TransactionsTable"
import type { TransactionsSortableAccessorKey } from "./constants"
import { TRANSACTIONS_SEARCH_PLACEHOLDER, accessorKeyToSortField, sortFieldToAccessorKey } from "./constants"
import { useTransactionsSearchStore } from "./stores/useTransactionsSearchStore"
import type { GlTransactionDialogMode } from "./types/dialog"
import type { TransactionsSearchParams } from "./types/searchParams"

import { Button } from "@/components/common/Button"
import { TableHeaderActions } from "@/components/Tables/TableHeaderActions"
import { PermissionActionType } from "@/constants/permissions"
import { type GlTransactionRow, GlTransactionRowSortField, SortingOrder } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { queryClient } from "@/providers/GraphqlRouterProvider"

export const TransactionsContainer: FC = () => {
  const navigate = useNavigate()
  const currentSearch = useSearch({ from: "/app/accounting/_layout/gl-transactions" })
  const { hasPermissions } = usePermissionManager()
  const hasMaintenancePermission = hasPermissions([PermissionActionType.GL_TRANSACTION_MAINTENANCE])
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false)
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [activeDialog, setActiveDialog] = useState<{ mode: GlTransactionDialogMode; glTransactionId: string } | null>(
    null
  )
  const [documentRow, setDocumentRow] = useState<GlTransactionRow | null>(null)

  const { field, order, query, transactionType, statuses, postedByType, dateType, dateFrom, dateTo } = currentSearch

  const data = useTransactionsSearchStore((state) => state.data)
  const isFetching = useTransactionsSearchStore((state) => state.fetching)
  const executeSearch = useTransactionsSearchStore((state) => state.executeSearch)
  const loadMore = useTransactionsSearchStore((state) => state.loadMore)
  const clearStore = useTransactionsSearchStore((state) => state.clearStore)

  const searchFilters = useMemo(
    () => ({ query, field, order, transactionType, statuses, postedByType, dateType, dateFrom, dateTo }),
    [query, field, order, transactionType, statuses, postedByType, dateType, dateFrom, dateTo]
  )

  useEffect(() => {
    executeSearch(queryClient, searchFilters)
  }, [searchFilters, executeSearch])

  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  const tableContainerRef = useRef<HTMLDivElement>(null)

  const handleLoadMore = useCallback(() => {
    loadMore(queryClient, searchFilters)
  }, [loadMore, searchFilters])

  const handleScroll = useScrollLoadMore({
    fetching: isFetching,
    loadMore: handleLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const sorting = useMemo(() => {
    if (!field) return []
    const accessorKey = sortFieldToAccessorKey.get(field)
    if (!accessorKey) return []
    return [{ id: accessorKey, desc: order === SortingOrder.Desc }]
  }, [field, order])

  const handleOpenDialog = useCallback((mode: GlTransactionDialogMode, row: GlTransactionRow) => {
    setActiveDialog({ mode, glTransactionId: row.glTransactionId })
  }, [])

  const handleDialogClose = useCallback(() => {
    setActiveDialog(null)
  }, [])

  const handleDocumentsOpen = useCallback((row: GlTransactionRow) => {
    setDocumentRow(row)
  }, [])

  const handleDocumentsClose = useCallback(() => {
    setDocumentRow(null)
  }, [])

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterFn) => {
      const newSorting = typeof updaterFn === "function" ? updaterFn(sorting) : updaterFn
      if (newSorting.length) {
        const sort = newSorting[0]
        const sortField =
          accessorKeyToSortField.get(sort.id as TransactionsSortableAccessorKey) ??
          GlTransactionRowSortField.TransactionDate

        navigate({
          search: (prev) => ({
            ...prev,
            field: sortField,
            order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
          }),
          to: "/app/accounting/gl-transactions",
          replace: true,
        })
      }
    },
    [navigate, sorting]
  )

  return (
    <Box className='flex size-full flex-col'>
      <Paper elevation={0} className='flex min-h-0 grow flex-col gap-4 overflow-hidden bg-white'>
        <TransactionsFilterDrawer
          showFilterDrawer={filterDrawerOpen}
          toggleDrawer={() => setFilterDrawerOpen((prev) => !prev)}
        />

        <TableHeaderActions<TransactionsSearchParams>
          currentSearch={currentSearch}
          placeholder={TRANSACTIONS_SEARCH_PLACEHOLDER}
          navigateTo='/app/accounting/gl-transactions'
          testIdPrefix='transactions'
        >
          <Button
            appearance='outlined'
            startIcon={<FilterAltOutlined />}
            onClick={() => setFilterDrawerOpen(true)}
            rootElementName='button'
            data-testid='transactions.filter-button'
          >
            Filter
          </Button>

          <Box className='flex items-center gap-2'>
            <Button
              appearance='outlined'
              startIcon={<Download2FilledOutlined />}
              onClick={() => setIsExportDialogOpen(true)}
              rootElementName='button'
              data-testid='transactions.export-button'
            >
              Export
            </Button>
            {hasMaintenancePermission && (
              <Button
                appearance='contained'
                startIcon={<AddOutlined />}
                onClick={() => setIsAddDialogOpen(true)}
                rootElementName='button'
                data-testid='transactions.add-gl-entry-button'
              >
                General Ledger Entry
              </Button>
            )}
          </Box>
        </TableHeaderActions>

        {isExportDialogOpen && <ExportGlTransactionsDialog onClose={() => setIsExportDialogOpen(false)} />}

        {isAddDialogOpen && hasMaintenancePermission && (
          <AddGlTransactionDialog
            onClose={() => setIsAddDialogOpen(false)}
            onSuccess={() => executeSearch(queryClient, searchFilters, true)}
          />
        )}

        {activeDialog && (
          <EditGlTransactionDialog
            glTransactionId={activeDialog.glTransactionId}
            mode={activeDialog.mode}
            onClose={handleDialogClose}
            onSuccess={() => {
              handleDialogClose()
              executeSearch(queryClient, searchFilters, true)
            }}
          />
        )}

        {documentRow && (
          <TransactionDocumentsDialog
            row={documentRow}
            onClose={handleDocumentsClose}
            onDocumentDeleted={() => executeSearch(queryClient, searchFilters, true)}
          />
        )}

        <TransactionsFilterChips />

        <Box className='flex flex-1 flex-col overflow-y-auto'>
          <TransactionsTable
            data={data}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            loading={isFetching}
            scrollHandler={handleScroll}
            tableContainerRef={tableContainerRef}
            hasMaintenancePermission={hasMaintenancePermission}
            onEdit={(row) =>
              handleOpenDialog(hasMaintenancePermission && !row.glTransaction?.isReadOnly ? "edit" : "view", row)
            }
            onReverse={(row) => handleOpenDialog("reverse", row)}
            onCancel={(row) => handleOpenDialog("cancel", row)}
            onDocumentsOpen={handleDocumentsOpen}
          />
        </Box>
      </Paper>
    </Box>
  )
}
